import cx from 'clsx';
import { format } from 'date-fns';
import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as AddIcon } from '@assets/svg/add.svg';

import StatusTypeItem from '@components/common/StatusTypeItem/StatusTypeItem';
import Button from '@components/uikit/Button/Button';

import { MODAL_NAMES } from '@constants/modalRenderers';

import useTranslation from '@hooks/useTranslation';

import type { IActionItem } from '@model/actionItem';
import type { IInstallationPoint } from '@model/installationPoint';

import { useAvailableEquipmentTypesQuery } from '@services/useAvailableEquipmentTypesQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';
import { useModalActions } from '@store/useModalStore';

import { getActionsItemAddButtonConfig } from '../../helpers/getActionsItemAddButtonConfig';
import { useSensorAiByInstPointQuery } from '../../services/useSensorAiByInstPointQuery';
import HistoryTableSensorAiSkeleton from './HistoryTableSensorAiSkeleton';
import style from './style.module.scss';

interface IProps {
  dateFormat: string;
  point?: IInstallationPoint;
  isPointLoading?: boolean;
}

const HistoryTableSensorAi: React.FC<IProps> = ({ dateFormat, point, isPointLoading }) => {
  const { t } = useTranslation();
  const { customerId, pointId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { setActiveModal } = useModalActions();

  const locationSelectValue = selectAppLocationSelectValue();

  const { data: actionItemsData, isLoading: isActionItemsLoading } = useSensorAiByInstPointQuery({ pointId });

  const { data: equipmentTypesListData } = useAvailableEquipmentTypesQuery({
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });

  const { data: profileData } = useUserProfileQuery();

  const tableList = actionItemsData?.list || [];

  const getActionInfo = (actionItem: IActionItem) => {
    if (+actionItem.state === 5) {
      return t('No action is required.');
    }

    return actionItem.activeReport?.cause || actionItem.activeReport?.text || '';
  };

  const getDates = (actionItem: IActionItem) => {
    let currentDate = format(actionItem.timeCreated, dateFormat);

    if (actionItem.closeAt) {
      currentDate += ` - ${format(actionItem.closeAt, dateFormat)}`;
    }

    return currentDate;
  };

  const { canCreateNewAI, aiType } = getActionsItemAddButtonConfig(point);

  const showActionsItemAddButton =
    canCreateNewAI &&
    !!profileData?.userAccess?.manageData &&
    !!point &&
    !isPointLoading &&
    !isActionItemsLoading;

  return (
    <>
      <table cellSpacing="0" cellPadding="0" className={style.historySensorAiTable}>
        <thead>
          <tr>
            <th>{t('Name/date')}</th>
            <th>{t('Action')}</th>
            <th className={style.alignRightTd}>{t('Status')}</th>
          </tr>
        </thead>
        <tbody>
          {isPointLoading || isActionItemsLoading ? (
            <HistoryTableSensorAiSkeleton />
          ) : tableList.length ? (
            tableList.map((actionItem) => (
              <tr
                key={uuidv4()}
                onClick={() =>
                  navigate(`/customer/${customerId}/ai/${actionItem.id}`, { state: { prevPage: pathname } })
                }
              >
                <td>
                  <div className={style.infoTitle}>{actionItem.title}</div>
                  <p className={style.infoDate}>{getDates(actionItem)}</p>
                </td>
                <td>
                  <p
                    className={style.infoText}
                    dangerouslySetInnerHTML={{ __html: getActionInfo(actionItem) }}
                  />
                </td>
                <td className={style.alignRightTd}>
                  <StatusTypeItem item={actionItem} onlyTitle={true} hideTroubleshooting={true} />
                </td>
              </tr>
            ))
          ) : (
            <tr className={style.emptyRow}>
              <td className={style.emptyRowBlock} colSpan={3}>
                {t('No action items info')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className={style.buttonsContainer}>
        {showActionsItemAddButton && (
          <Button
            type="secondary"
            text={t('Add Action Item')}
            onClick={() => {
              const equipmentTypeItem = equipmentTypesListData?.find((item) => item.id === point.pointType);

              setActiveModal({
                name: MODAL_NAMES.addActionItemModal,
                options: {
                  filledData: {
                    installationPoint: {
                      id: point.id,
                      label: point.name,
                      facilityId: point.equipment?.location?.facilityId,
                      facilityOffset: point.equipment?.location?.facilityOffset,
                    },
                    aiType,
                    equipmentType: equipmentTypeItem,
                  },
                },
              });
            }}
            leftElement={<AddIcon className={style.addIcon} />}
          />
        )}
        {actionItemsData?.showViewAll && (
          <Link
            to={`/customer/${customerId}/ai/${tableList[0]?.id}/history`}
            state={{ prevPage: pathname }}
            className={style.viewHistoryBtn}
          >
            {t('View all history')}
          </Link>
        )}
      </div>
    </>
  );
};

export default HistoryTableSensorAi;
